import { encodeGraphQLResponse, splitEncoding } from '@contentful/content-source-maps';
import type { GraphQLFullResponse } from '@/lib/graphql/apq';

/**
 * Encode a GraphQL response with Content Source Maps for Contentful Live Preview.
 * The encoding embeds invisible metadata into string fields that the SDK reads
 * to enable inspector mode (click-to-edit in Contentful).
 */
export function encodePreviewResponse<T>(response: GraphQLFullResponse<T>): T {
  if (!response.extensions) return response.data;
  const encoded = encodeGraphQLResponse({
    data: response.data,
    extensions: response.extensions,
  });
  return encoded.data as T;
}

/** Zero-width / BOM chars that preview encoding can leave on config strings after stega decode. */
const PREVIEW_CONFIG_INVISIBLE_CHARS = /[\u200B-\u200D\uFEFF]/g;

/**
 * Strip Content Source Maps encoding from a CMS configuration string.
 *
 * In preview mode, Content Source Maps embed invisible Unicode markers into
 * ALL string fields for inspector mode. This is correct for text displayed
 * in the DOM (headings, body copy) but breaks strings used as configuration
 * — CSS color values, alignment enums, type selectors — because strict
 * equality checks and CSS parsers reject the hidden characters.
 *
 * Use on any adapted field that is NOT rendered as visible text.
 */
export function cleanCmsConfigValue(value: string | null | undefined): string | null {
  if (!value) return null;
  const stripped = splitEncoding(value).cleaned.replace(PREVIEW_CONFIG_INVISIBLE_CHARS, '');
  return stripped || null;
}

/**
 * Derive the Contentful SDK locale from a URL locale path segment.
 *
 * The Live Preview SDK must subscribe to the locale where content is actually
 * stored in the Contentful space. This matches Contentful's own
 * `locale_to_country` preview token mapping (e.g. `en-US` → `us-en`).
 *
 * This differs from `convertLocaleToApiParams` which uses the internal routing
 * config (Gallet maps US to `en-GB` for content fallback), but the SDK needs
 * the **source** locale to detect edits instantly.
 */
export function getContentfulSdkLocale(urlLocale: string): string {
  const [country, langSlug] = urlLocale.split('-');
  if (langSlug === 'en') return `en-${country.toUpperCase()}`;
  if (langSlug === 'zht') return 'zh-Hant';
  return langSlug;
}
